/**
 * <recipe-video> — click-to-load for sections/recipe-video.liquid.
 *
 * The section renders a poster and a play button, and keeps the real player
 * (an uploaded <video> or a YouTube/Vimeo <iframe>) inside a <template>.
 * A <template>'s contents are inert, so nothing is downloaded and no
 * third-party request is made until the shopper clicks. This element does the
 * swap and then moves focus into the player so a keyboard user is not dropped
 * back at the top of the page.
 *
 * assets/section-brand-video.js does the same job for sections/brand-video.liquid
 * and is deliberately not reused: it is bound to that section's class names and
 * parses the provider and video id out of the raw URL in JavaScript, which this
 * section does in Liquid instead. Worth folding both into a shared
 * component-video-embed pair later — see the build report.
 *
 * Below 769px the recipe body stacks, and this section follows the whole body
 * in the template — so the player would sit under the commerce rail. The
 * mobile frame puts it directly after the Method. The shell is moved there
 * and put back at 769px, where the player is a full-width band under the
 * two-column body. The move is undone before a theme-editor section reload
 * removes the original wrapper, so the player is not left behind in the body.
 */
if (!customElements.get('recipe-video')) {
  customElements.define(
    'recipe-video',
    class RecipeVideo extends HTMLElement {
      constructor() {
        super();
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onLayoutChange = this.onLayoutChange.bind(this);
        this.onSectionUnload = this.onSectionUnload.bind(this);
      }

      connectedCallback() {
        this.trigger = this.querySelector('.recipe-video__trigger');
        this.trigger?.addEventListener('click', this.onTriggerClick);

        this.shell = this.closest('.recipe-video');
        this.home = this.shell?.parentNode ?? null;
        this.section = this.home?.closest?.('.shopify-section') ?? null;
        this.layoutQuery = window.matchMedia('(max-width: 768px)');
        this.layoutQuery.addEventListener('change', this.onLayoutChange);
        this.section?.addEventListener('shopify:section:unload', this.onSectionUnload);
        this.place();
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onTriggerClick);
        this.trigger = null;
        this.layoutQuery?.removeEventListener('change', this.onLayoutChange);
        this.section?.removeEventListener('shopify:section:unload', this.onSectionUnload);
      }

      onLayoutChange() {
        this.place();
      }

      onSectionUnload(event) {
        if (event.target !== this.section) {
          return;
        }

        this.restore();
      }

      place() {
        if (!this.shell || !this.home) {
          return;
        }

        const method = document.querySelector('.recipe-body__method');

        if (this.layoutQuery.matches && method) {
          if (method.nextElementSibling !== this.shell) {
            method.after(this.shell);
          }

          return;
        }

        this.restore();
      }

      restore() {
        if (!this.shell || !this.home || this.shell.parentNode === this.home) {
          return;
        }

        this.home.append(this.shell);
      }

      async onTriggerClick() {
        const media = this.querySelector('.recipe-video__media');
        const template = this.querySelector('[data-recipe-video-embed]');

        if (!media || !template) {
          return;
        }

        // Replaces the poster and the button in one go; the media box keeps its
        // ratio, radius and the block's shopify_attributes.
        media.replaceChildren(template.content.cloneNode(true));
        template.remove();

        this.trigger?.removeEventListener('click', this.onTriggerClick);
        this.trigger = null;

        const player = media.querySelector('video, iframe');

        if (!player) {
          return;
        }

        // The player replaces the poster in place, so it is already where the
        // shopper just clicked — focus() needs no scroll suppression.
        player.focus();

        if (typeof player.play !== 'function') {
          return;
        }

        // The click is a user gesture, so an unmuted play() is allowed here
        // even where the autoplay attribute alone would be blocked. The iframe
        // path carries autoplay in its own query string and has no play().
        try {
          await player.play();
        } catch (error) {
          // A browser that still refuses playback leaves the native controls
          // showing, which is the correct fallback. Nothing to recover.
        }
      }
    },
  );
}
